import { encrypt, decrypt } from "@/js/encrypt";

/**
 * 按需加解密（默认关闭，仅带 apiCrypto 的请求会处理）
 *
 * 请求加密：不传 JSON 包装，**HTTP 体或查询串上仅为密文字符串**（Base64 等，由 encrypt 产出）
 * - POST / PUT / PATCH / DELETE（有 body）：body = encrypt(明文)，Content-Type: text/plain;charset=UTF-8
 * - GET / DELETE（仅有 params）：将 encrypt(JSON.stringify(params)) 追加到 URL（? 或 &），并清空 params
 *
 * - apiCrypto: true | 'all'  → 请求加密 + 响应解密
 * - apiCrypto: 'request'    → 仅加密请求
 * - apiCrypto: 'response'   → 仅解密响应
 * - skipApiCrypto: true     → 本条请求强制跳过
 *
 * 若后端要求「固定查询参数名」承载密文，可设置 cryptoQueryParam，例如 cryptoQueryParam: 'data'
 * 此时 GET 使用 params: { data: cipher }，不再往 URL 裸追加。
 *
 * 环境变量 VUE_APP_ENABLE_API_CRYPTO=false 时全局关闭加解密。
 */
const masterDisabled = process.env.VUE_APP_ENABLE_API_CRYPTO === "false";

function isAbsoluteRequestUrl(url) {
  return /^https?:\/\//i.test(url || "");
}

function getCryptoMode(config) {
  if (!config || masterDisabled || config.skipApiCrypto) {
    return { request: false, response: false };
  }
  const v = config.apiCrypto;
  if (v === true || v === "all") {
    return { request: true, response: true };
  }
  if (v === "request") {
    return { request: true, response: false };
  }
  if (v === "response") {
    return { request: false, response: true };
  }
  return { request: false, response: false };
}

export function shouldSkipRequestEncryption(config) {
  const m = getCryptoMode(config);
  if (!m.request) return true;
  const url = config.url || "";
  if (isAbsoluteRequestUrl(url)) return true;
  if (typeof FormData !== "undefined" && config.data instanceof FormData)
    return true;
  return false;
}

export function shouldSkipResponseDecryption(config) {
  const m = getCryptoMode(config);
  if (!m.response) return true;
  const url = config.url || "";
  if (isAbsoluteRequestUrl(url)) return true;
  if (config.responseType === "blob" || config.responseType === "arraybuffer")
    return true;
  return false;
}

function tryDecryptToObject(cipherText) {
  const plain = decrypt(cipherText.trim());
  return JSON.parse(plain);
}

/** 将业务 data 转为待加密的明文字符串 */
function buildPlaintextForEncrypt(data) {
  if (data == null || data === "") return null;
  if (typeof data === "string") {
    try {
      return JSON.stringify(JSON.parse(data));
    } catch {
      return data;
    }
  }
  if (typeof data === "object") {
    return JSON.stringify(data);
  }
  return String(data);
}

function setPlaintextBody(config, plaintext) {
  if (plaintext == null) return;
  const cipher = encrypt(plaintext);
  config.data = cipher;
  config.headers = config.headers || {};
  config.headers["Content-Type"] = "text/plain;charset=UTF-8";
}

function appendCipherToUrl(config, cipher) {
  let url = config.url || "";
  const encoded = encodeURIComponent(cipher);
  const join = url.includes("?") ? "&" : "?";
  config.url = `${url}${join}${encoded}`;
  config.params = undefined;
}

/**
 * 请求加密：密文直传，无 JSON 包装
 */
export function applyRequestEncryption(config) {
  if (shouldSkipRequestEncryption(config)) return config;
  const method = (config.method || "get").toLowerCase();

  const queryParamName = config.cryptoQueryParam;

  if (method === "get") {
    if (config.params && Object.keys(config.params).length > 0) {
      const payload = JSON.stringify(config.params);
      const cipher = encrypt(payload);
      if (queryParamName) {
        config.params = { [queryParamName]: cipher };
      } else {
        appendCipherToUrl(config, cipher);
      }
    }
    return config;
  }

  if (method === "delete") {
    if (config.params && Object.keys(config.params).length > 0) {
      const payload = JSON.stringify(config.params);
      const cipher = encrypt(payload);
      if (queryParamName) {
        config.params = { [queryParamName]: cipher };
      } else {
        appendCipherToUrl(config, cipher);
      }
    }
    if (config.data != null && config.data !== "") {
      const plaintext = buildPlaintextForEncrypt(config.data);
      setPlaintextBody(config, plaintext);
    }
    return config;
  }

  if (["post", "put", "patch"].includes(method)) {
    if (config.data == null || config.data === "") return config;
    const plaintext = buildPlaintextForEncrypt(config.data);
    setPlaintextBody(config, plaintext);
  }
  return config;
}

/**
 * 响应解密：支持整段密文字符串，或 { code, data: 密文 } 中 data 为业务密文
 */
export function applyResponseDecryption(response) {
  const config = response.config || {};
  if (shouldSkipResponseDecryption(config)) return response.data;

  let raw = response.data;

  if (typeof Blob !== "undefined" && raw instanceof Blob) return raw;
  if (raw == null) return raw;

  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return raw;
    try {
      return tryDecryptToObject(t);
    } catch {
      return raw;
    }
  }

  if (typeof raw === "object" && typeof raw.data === "string") {
    const cipher = raw.data.trim();
    if (!cipher) return raw;
    try {
      const inner = tryDecryptToObject(cipher);
      return { ...raw, data: inner };
    } catch {
      return raw;
    }
  }

  return raw;
}
